import { trackEvent } from '@/utils/analytics';

/**
 * Track when a user connects a wearable device
 * @param deviceId - The ID or type of the device
 * @param deviceType - The type of wearable device
 * @param properties - Additional properties to track
 */
export const trackDeviceConnected = (
  deviceId: string,
  deviceType: 'whoop' | 'oura' | 'garmin' | 'fitbit' | 'apple-watch' | 'samsung' | string,
  properties?: Record<string, any>
) => {
  trackEvent('device_connected', {
    device_id: deviceId,
    device_type: deviceType,
    connection_method: properties?.connectionMethod || 'api',
    ...properties
  });
};

/**
 * Track when a user disconnects a wearable device
 * @param deviceId - The ID or type of the device
 * @param deviceType - The type of wearable device
 * @param properties - Additional properties to track
 */
export const trackDeviceDisconnected = (
  deviceId: string,
  deviceType: 'whoop' | 'oura' | 'garmin' | 'fitbit' | 'apple-watch' | 'samsung' | string,
  properties?: Record<string, any>
) => {
  trackEvent('device_disconnected', {
    device_id: deviceId,
    device_type: deviceType,
    disconnect_reason: properties?.reason || 'user_initiated',
    ...properties
  });
};

/**
 * Track when data is synced from a wearable device
 * @param deviceId - The ID or type of the device
 * @param deviceType - The type of wearable device
 * @param dataTypes - The types of data that were synced
 * @param properties - Additional properties to track
 */
export const trackDataSynced = (
  deviceId: string,
  deviceType: 'whoop' | 'oura' | 'garmin' | 'fitbit' | 'apple-watch' | 'samsung' | string,
  dataTypes: string[],
  properties?: Record<string, any>
) => {
  trackEvent('data_synced', {
    device_id: deviceId,
    device_type: deviceType,
    data_types: dataTypes,
    sync_method: properties?.syncMethod || 'automatic',
    data_points: properties?.dataPoints,
    sync_duration: properties?.syncDuration,
    ...properties
  });
};

/**
 * Track when a user grants permissions for device data
 * @param deviceId - The ID or type of the device
 * @param deviceType - The type of wearable device
 * @param permissions - The permissions that were granted
 * @param properties - Additional properties to track
 */
export const trackPermissionGranted = (
  deviceId: string,
  deviceType: 'whoop' | 'oura' | 'garmin' | 'fitbit' | 'apple-watch' | 'samsung' | string,
  permissions: string[],
  properties?: Record<string, any>
) => {
  trackEvent('permission_granted', {
    device_id: deviceId,
    device_type: deviceType,
    permissions,
    ...properties
  });
};

/**
 * Track when a user views their health data
 * @param dataType - The type of health data being viewed
 * @param timeRange - The time range of the data
 * @param properties - Additional properties to track
 */
export const trackHealthDataViewed = (
  dataType: 'heart_rate' | 'sleep' | 'steps' | 'activity' | 'calories' | 'all' | string,
  timeRange: 'day' | 'week' | 'month' | 'year' | string,
  properties?: Record<string, any>
) => {
  trackEvent('health_data_viewed', {
    data_type: dataType,
    time_range: timeRange,
    device_source: properties?.deviceSource,
    view_duration: properties?.viewDuration,
    ...properties
  });
};

/**
 * Track when a user exports their health data
 * @param dataType - The type of health data being exported
 * @param exportFormat - The format of the export
 * @param properties - Additional properties to track
 */
export const trackHealthDataExported = (
  dataType: 'heart_rate' | 'sleep' | 'steps' | 'activity' | 'calories' | 'all' | string,
  exportFormat: 'csv' | 'json' | 'pdf' | string,
  properties?: Record<string, any>
) => {
  trackEvent('health_data_exported', {
    data_type: dataType,
    export_format: exportFormat,
    time_range: properties?.timeRange || 'all',
    device_source: properties?.deviceSource,
    ...properties
  });
};

/**
 * Track when a user shares their health data
 * @param dataType - The type of health data being shared
 * @param shareTarget - Where the data is being shared to
 * @param properties - Additional properties to track
 */
export const trackHealthDataShared = (
  dataType: 'heart_rate' | 'sleep' | 'steps' | 'activity' | 'calories' | 'all' | string,
  shareTarget: 'social' | 'healthcare_provider' | 'coach' | 'friend' | string,
  properties?: Record<string, any>
) => {
  trackEvent('health_data_shared', {
    data_type: dataType,
    share_target: shareTarget,
    time_range: properties?.timeRange || 'day',
    device_source: properties?.deviceSource,
    ...properties
  });
};

/**
 * Track when a user sets up a wearable notification
 * @param notificationType - The type of notification
 * @param deviceType - The type of wearable device
 * @param properties - Additional properties to track
 */
export const trackWearableNotificationSetup = (
  notificationType: 'activity' | 'sleep' | 'heart_rate' | 'goal' | string,
  deviceType: 'whoop' | 'oura' | 'garmin' | 'fitbit' | 'apple-watch' | 'samsung' | string,
  properties?: Record<string, any>
) => {
  trackEvent('wearable_notification_setup', {
    notification_type: notificationType,
    device_type: deviceType,
    frequency: properties?.frequency || 'daily',
    ...properties
  });
};